import * as React from 'react';
import styled, { css } from 'styled-components';

import type { QueryValidationState, StreamDataRouting } from 'views/components/searchbar/queryvalidation/types';
import { Explanation } from 'views/components/searchbar/queryvalidation/QueryValidation';
import { Icon, IfPermitted } from 'components/common';
import SelectedInternalDataLakeProvider from 'data-lake/contexts/SelectedInternalDataLakeProvider';
import PreviewInternalLakeLogsButton from 'data-lake/components/PreviewInternalLakeLogsButton';

import DataLakeSearchRetrieve from './DataLakeSearchRetrieve';

const StyledIcon = styled(Icon)(
  ({ theme }) => css`
    color: ${theme.colors.variant.info};
    margin-right: ${theme.spacings.xxs};
  `,
);

const DataLakePreviewLinks = ({ streams }: { streams: Array<StreamDataRouting> }) => (
  <>
    {streams.map(({ stream_name: streamName, stream_id: streamId }, index) => (
      <span key={streamId}>
        {index !== 0 && <br />}
        <PreviewInternalLakeLogsButton bsStyle="link" streamId={streamId} bsSize="md" target="_blank">
          From stream <i>{streamName}</i>
        </PreviewInternalLakeLogsButton>
      </span>
    ))}
  </>
);

type Props = {
  validationState: QueryValidationState;
};

const DataLakeQueryValidation = ({ validationState }: Props) => {
  const hasDataLakeData = validationState.status === 'INFO' && validationState.context?.data_routed_streams.length > 0;

  if (!hasDataLakeData) {
    return null;
  }

  const streamIds = validationState.context?.data_routed_streams.map((data) => data.stream_id);
  const timeRange = validationState.context?.searched_time_range;

  return (
    <SelectedInternalDataLakeProvider>
      <Explanation>
        <span>
          <p>
            <StyledIcon name="database" /> Additional data that meets this search criteria may exist in the Data Lake.
          </p>
          <p>
            <b>Preview Logs</b>
            <br />
            <DataLakePreviewLinks streams={validationState.context?.data_routed_streams} />
          </p>
          <IfPermitted permissions={['data_lake_archive:restore']}>
            <p>
              <b>Retrieve Logs</b>
              <br />
              <DataLakeSearchRetrieve streamIds={streamIds} timeRange={timeRange} />
            </p>
          </IfPermitted>
        </span>
      </Explanation>
    </SelectedInternalDataLakeProvider>
  );
};

export default DataLakeQueryValidation;
